import { useEffect, useState } from 'react';
import { Divider, IconButton, List, ListItem, ListItemText, Box, Stack } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations.tsx';
import type { MediaAssetDto } from '@/types/dtos/media/media.asset.dto.ts';
import type { MediaDto } from '@/types/dtos/media/media.dto.ts';
import type { MediaParentEntity } from '@/types/dtos/media/media-parent-entity.enum.ts';
import { format, parseISO } from 'date-fns';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DeleteIcon from '@mui/icons-material/Delete';
import { useGetMediaDownloadUrlMutation } from '@/state/queries/media/use-get-media-download-url.ts';
import { useNotifications } from '@/hooks/use-notifications';
import { downloadFile } from '@/lib/download-file.ts';
import { useDeleteMediaAssetMutation } from '@/state/mutations/media/use-delete-media-asset.ts';
import { useApproveMediaAssetMutation } from '@/state/mutations/media/use-approve-media-asset.ts';
import ConfirmationModal from '@/components/common/confirmation-modal.tsx';
import { useMediaPermissions } from '@/hooks/use-media-permissions';
import { MediaAssetApprovalControl } from '@/components/media/media-asset-approval-control.tsx';
import { MediaProcessingInsightBadge } from '@/components/media/media-processing-insight-badge.tsx';
import { getProcessingInsightForAsset, isAssetApprovable } from '@/helpers/media-processing-insight.helper';
import { useMediaProcessingInstructionsContext } from '@/hooks/use-media-processing-instructions-context';

interface MediaAssetListProps {
  mediaId: string;
  mediaTitle: string;
  mediaAssets: MediaAssetDto[];
  parentId?: string;
  parentType?: MediaParentEntity;
  /** When set, enables approval controls using permissions from the media item. */
  media?: MediaDto;
}

export default function MediaAssetList({
  mediaId,
  mediaTitle,
  mediaAssets,
  parentId,
  parentType,
  media,
}: MediaAssetListProps) {
  const t = useTranslations();
  const notification = useNotifications();
  const permissions = useMediaPermissions(media);
  const [mediaAssetToDelete, setMediaAssetToDelete] = useState<MediaAssetDto | null>(null);
  const [approvalOverrides, setApprovalOverrides] = useState<Record<number, boolean>>({});
  const { mutateAsync: getMediaDownloadUrlMutation, isPending: isDownloadingPending } =
    useGetMediaDownloadUrlMutation();
  const { mutateAsync: deleteMediaAssetMutation, isPending: isDeletingPending } = useDeleteMediaAssetMutation();
  const { mutateAsync: approveMediaAssetMutation } = useApproveMediaAssetMutation();

  const resolvedParentId = parentId ?? media?.parent?.id;
  const resolvedParentType = parentType ?? media?.parentType;
  const instructionsContextBase = useMediaProcessingInstructionsContext(media);

  useEffect(() => {
    setApprovalOverrides((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const next = { ...prev };
      let changed = false;
      for (const [versionKey, approved] of Object.entries(prev)) {
        const version = Number(versionKey);
        const asset = mediaAssets.find((item) => item.version === version);
        if (asset && (asset.isApproved ?? false) === approved) {
          delete next[version];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [mediaAssets, mediaId]);

  const resolveAssetIsApproved = (asset: MediaAssetDto) =>
    approvalOverrides[asset.version] ?? asset.isApproved ?? false;

  const handleDownload = async (mediaAsset: MediaAssetDto) => {
    try {
      const { url } = await getMediaDownloadUrlMutation(mediaAsset.uri);
      downloadFile(url);
    } catch {
      notification.notifyError(t('media.notification.downloadError'));
    }
  };

  const handleDelete = async () => {
    if (!mediaAssetToDelete) return;
    try {
      await deleteMediaAssetMutation({ mediaId, assetVersion: mediaAssetToDelete.version });
      notification.notifySuccess(
        t('media.notification.deleteAssetSuccess', {
          version: mediaAssetToDelete.version.toString(),
          title: mediaTitle,
        }),
      );
    } catch {
      notification.notifyError(t('media.notification.deleteAssetError'));
    }
  };

  const handleAssetApproval = async (assetVersion: number, currentApprovedState: boolean) => {
    const asset = mediaAssets.find((item) => item.version === assetVersion);
    if (!currentApprovedState && asset && !isAssetApprovable(asset)) {
      return;
    }

    const nextApproved = !currentApprovedState;
    setApprovalOverrides((prev) => ({ ...prev, [assetVersion]: nextApproved }));
    try {
      await approveMediaAssetMutation({
        mediaId,
        assetVersion,
        isApproved: nextApproved,
        parentId: resolvedParentId,
        parentType: resolvedParentType,
      });

      notification.notifySuccess(
        t(
          !currentApprovedState ? 'media.notification.approveAssetSuccess' : 'media.notification.unapproveAssetSuccess',
          {
            version: assetVersion.toString(),
            title: mediaTitle,
          },
        ),
      );
    } catch {
      setApprovalOverrides((prev) => {
        const next = { ...prev };
        delete next[assetVersion];
        return next;
      });
      notification.notifyError(
        t(!currentApprovedState ? 'media.notification.approveAssetError' : 'media.notification.unapproveAssetError', {
          version: assetVersion.toString(),
          title: mediaTitle,
        }),
      );
    }
  };

  return (
    <List sx={{ width: '100%' }}>
      {mediaAssets.map((mediaAsset, index) => (
        <Box key={mediaAsset.uri}>
          <ListItem
            sx={{ p: '0.6rem 0.8rem 0.6rem 0.8rem', alignItems: 'flex-start' }}
            secondaryAction={
              <Stack direction="row" spacing={0.5} alignItems="center">
                <MediaAssetApprovalControl
                  isApproved={resolveAssetIsApproved(mediaAsset)}
                  canApprove={permissions.canApprove}
                  isApprovable={isAssetApprovable(mediaAsset)}
                  onToggle={() => handleAssetApproval(mediaAsset.version, resolveAssetIsApproved(mediaAsset))}
                />
                {permissions.canDownload ? (
                  <IconButton
                    aria-label="download"
                    onClick={() => handleDownload(mediaAsset)}
                    loading={isDownloadingPending}
                  >
                    <FileDownloadIcon />
                  </IconButton>
                ) : null}
                <IconButton
                  aria-label="delete"
                  onClick={() => setMediaAssetToDelete(mediaAsset)}
                  loading={isDeletingPending}
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
            }
          >
            <ListItemText
              primary={
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <span>{`${t('common.version')} ${mediaAsset.version}`}</span>
                  <MediaProcessingInsightBadge
                    insight={getProcessingInsightForAsset(mediaAsset)}
                    instructionsContext={
                      instructionsContextBase
                        ? {
                            ...instructionsContextBase,
                            assetVersion: mediaAsset.version,
                          }
                        : undefined
                    }
                  />
                </Stack>
              }
              secondary={`${format(parseISO(mediaAsset.createdAt), 'dd MMM yyyy')}, ${t('media.uploadedBy')} ${mediaAsset.createdBy.firstName} ${mediaAsset.createdBy.lastName}`}
            />
          </ListItem>
          {index < mediaAssets.length - 1 && <Divider />}
        </Box>
      ))}
      <ConfirmationModal
        open={!!mediaAssetToDelete}
        title={t('media.deleteAssetModalTitle')}
        text={t('media.deleteAssetModalText', {
          version: mediaAssetToDelete?.version.toString() || '',
          title: mediaTitle,
        })}
        onConfirm={handleDelete}
        onCancel={() => setMediaAssetToDelete(null)}
      />
    </List>
  );
}
